import React, { useState } from 'react';
import { Phone, Mail, MapPin, MessageCircle, Upload, CheckCircle, AlertCircle } from 'lucide-react';
import { contactData, formSubmitMock } from '../data/mock';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    location: '',
    serviceType: '',
    message: '',
    privacy: false
  });
  
  const [formStatus, setFormStatus] = useState(null); // null, 'loading', 'success', 'error'
  const [formErrors, setFormErrors] = useState({});

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear error for this field
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.name.trim()) errors.name = 'Name erforderlich';
    if (!formData.phone.trim()) errors.phone = 'Telefonnummer erforderlich';
    if (!formData.location.trim()) errors.location = 'PLZ / Ort erforderlich';
    if (!formData.serviceType) errors.serviceType = 'Bitte wählen Sie einen Anlass';
    if (!formData.privacy) errors.privacy = 'Bitte akzeptieren Sie die Datenschutzerklärung';
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setFormStatus('loading');
    
    try {
      const result = await formSubmitMock(formData);
      setFormStatus('success');
      // Reset form after 3 seconds
      setTimeout(() => {
        setFormData({
          name: '',
          phone: '',
          location: '',
          serviceType: '',
          message: '',
          privacy: false
        });
        setFormStatus(null);
      }, 3000);
    } catch (error) {
      setFormStatus('error');
      setTimeout(() => setFormStatus(null), 3000);
    }
  };

  const handleWhatsApp = () => {
    window.open(`https://wa.me/${contactData.whatsapp}`, '_blank');
  };

  return (
    <div style={{ background: '#000000', position: 'relative' }}>
      <div className="pattern-overlay" />
      
      {/* Hero */}
      <section style={{ paddingTop: '140px', paddingBottom: '80px', position: 'relative' }}>
        <div className="container mx-auto px-4" style={{ maxWidth: '1200px' }}>
          <div style={{ textAlign: 'center', maxWidth: '700px', margin: '0 auto' }}>
            <h1 className="display-huge" style={{ 
              marginBottom: '24px',
              animation: 'fade-in-up 0.8s ease-out'
            }}>
              Kontakt in <span style={{ color: '#00A8CC' }}>60 Sekunden</span>
            </h1>
            <p className="body-large" style={{ 
              color: 'rgba(255, 255, 255, 0.7)',
              animation: 'fade-in-up 0.8s ease-out 0.2s both'
            }}>
              Schick uns kurz die Infos und wir melden uns sofort.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="pad-xlarge" style={{ position: 'relative' }}>
        <div className="container mx-auto px-4" style={{ maxWidth: '1200px' }}>
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
            {/* Contact Cards */}
            <div className="lg:col-span-2">
              <div className="flex flex-col gap-6">
                {/* Phone */}
                <div className="glass-card" style={{ animation: 'fade-in-up 0.6s ease-out' }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '12px',
                    background: 'rgba(0, 168, 204, 0.1)',
                    border: '1px solid rgba(0, 168, 204, 0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '16px'
                  }}>
                    <Phone size={24} style={{ color: '#00A8CC' }} />
                  </div>
                  <h3 className="heading-2" style={{ marginBottom: '8px' }}>Telefon</h3>
                  <a 
                    href={`tel:${contactData.phone}`}
                    style={{
                      color: 'rgba(255, 255, 255, 0.7)',
                      textDecoration: 'none',
                      fontSize: '16px',
                      transition: 'color 0.3s ease'
                    }}
                    onMouseEnter={(e) => e.target.style.color = '#00A8CC'}
                    onMouseLeave={(e) => e.target.style.color = 'rgba(255, 255, 255, 0.7)'}
                  >
                    {contactData.phone}
                  </a>
                </div>

                {/* WhatsApp */}
                <div className="glass-card" style={{ animation: 'fade-in-up 0.6s ease-out 0.1s both' }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '12px',
                    background: 'rgba(0, 168, 204, 0.1)',
                    border: '1px solid rgba(0, 168, 204, 0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '16px'
                  }}>
                    <MessageCircle size={24} style={{ color: '#00A8CC' }} />
                  </div>
                  <h3 className="heading-2" style={{ marginBottom: '8px' }}>WhatsApp</h3>
                  <button
                    onClick={handleWhatsApp}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: 'rgba(255, 255, 255, 0.7)',
                      fontSize: '16px',
                      cursor: 'pointer',
                      padding: 0,
                      transition: 'color 0.3s ease'
                    }}
                    onMouseEnter={(e) => e.target.style.color = '#00A8CC'}
                    onMouseLeave={(e) => e.target.style.color = 'rgba(255, 255, 255, 0.7)'}
                  >
                    {contactData.phone}
                  </button>
                </div>

                {/* Email */}
                <div className="glass-card" style={{ animation: 'fade-in-up 0.6s ease-out 0.2s both' }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '12px',
                    background: 'rgba(0, 168, 204, 0.1)',
                    border: '1px solid rgba(0, 168, 204, 0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '16px'
                  }}>
                    <Mail size={24} style={{ color: '#00A8CC' }} />
                  </div>
                  <h3 className="heading-2" style={{ marginBottom: '8px' }}>E-Mail</h3>
                  <a 
                    href={`mailto:${contactData.email}`}
                    style={{
                      color: 'rgba(255, 255, 255, 0.7)',
                      textDecoration: 'none',
                      fontSize: '16px',
                      transition: 'color 0.3s ease',
                      wordBreak: 'break-word'
                    }}
                    onMouseEnter={(e) => e.target.style.color = '#00A8CC'}
                    onMouseLeave={(e) => e.target.style.color = 'rgba(255, 255, 255, 0.7)'}
                  >
                    {contactData.email}
                  </a>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-3">
              <div className="glass-card" style={{ 
                padding: '40px',
                animation: 'fade-in-up 0.6s ease-out 0.3s both'
              }}>
                <h2 className="heading-1" style={{ marginBottom: '8px' }}>
                  Gutachten anfragen
                </h2>
                <p className="body-medium" style={{ 
                  color: 'rgba(255, 255, 255, 0.6)',
                  marginBottom: '32px'
                }}>
                  Antwort meist innerhalb kurzer Zeit.
                </p>

                <form onSubmit={handleSubmit}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Name */}
                    <div>
                      <label style={{
                        display: 'block',
                        fontSize: '14px',
                        fontWeight: '600',
                        color: 'rgba(255, 255, 255, 0.9)',
                        marginBottom: '8px'
                      }}>
                        Name *
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        style={{
                          width: '100%',
                          padding: '14px 16px',
                          background: 'rgba(255, 255, 255, 0.05)',
                          border: `1px solid ${formErrors.name ? '#ff4444' : 'rgba(255, 255, 255, 0.2)'}`,
                          borderRadius: '8px',
                          color: '#FFFFFF',
                          fontSize: '16px',
                          outline: 'none',
                          transition: 'border-color 0.3s ease'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#00A8CC'}
                        onBlur={(e) => e.target.style.borderColor = formErrors.name ? '#ff4444' : 'rgba(255, 255, 255, 0.2)'}
                      />
                      {formErrors.name && (
                        <span style={{ fontSize: '12px', color: '#ff4444', marginTop: '4px', display: 'block' }}>
                          {formErrors.name}
                        </span>
                      )}
                    </div>

                    {/* Phone */}
                    <div>
                      <label style={{
                        display: 'block',
                        fontSize: '14px',
                        fontWeight: '600',
                        color: 'rgba(255, 255, 255, 0.9)',
                        marginBottom: '8px'
                      }}>
                        Telefonnummer *
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        style={{
                          width: '100%',
                          padding: '14px 16px',
                          background: 'rgba(255, 255, 255, 0.05)',
                          border: `1px solid ${formErrors.phone ? '#ff4444' : 'rgba(255, 255, 255, 0.2)'}`,
                          borderRadius: '8px',
                          color: '#FFFFFF',
                          fontSize: '16px',
                          outline: 'none',
                          transition: 'border-color 0.3s ease'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#00A8CC'}
                        onBlur={(e) => e.target.style.borderColor = formErrors.phone ? '#ff4444' : 'rgba(255, 255, 255, 0.2)'}
                      />
                      {formErrors.phone && (
                        <span style={{ fontSize: '12px', color: '#ff4444', marginTop: '4px', display: 'block' }}>
                          {formErrors.phone}
                        </span>
                      )}
                    </div>

                    {/* Location */}
                    <div>
                      <label style={{
                        display: 'block',
                        fontSize: '14px',
                        fontWeight: '600',
                        color: 'rgba(255, 255, 255, 0.9)',
                        marginBottom: '8px'
                      }}>
                        PLZ / Ort *
                      </label>
                      <input
                        type="text"
                        name="location"
                        value={formData.location}
                        onChange={handleChange}
                        style={{
                          width: '100%',
                          padding: '14px 16px',
                          background: 'rgba(255, 255, 255, 0.05)',
                          border: `1px solid ${formErrors.location ? '#ff4444' : 'rgba(255, 255, 255, 0.2)'}`,
                          borderRadius: '8px',
                          color: '#FFFFFF',
                          fontSize: '16px',
                          outline: 'none',
                          transition: 'border-color 0.3s ease'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#00A8CC'}
                        onBlur={(e) => e.target.style.borderColor = formErrors.location ? '#ff4444' : 'rgba(255, 255, 255, 0.2)'}
                      />
                      {formErrors.location && (
                        <span style={{ fontSize: '12px', color: '#ff4444', marginTop: '4px', display: 'block' }}>
                          {formErrors.location}
                        </span>
                      )}
                    </div>

                    {/* Service Type */}
                    <div>
                      <label style={{
                        display: 'block',
                        fontSize: '14px',
                        fontWeight: '600',
                        color: 'rgba(255, 255, 255, 0.9)',
                        marginBottom: '8px'
                      }}>
                        Art des Anliegens *
                      </label>
                      <select
                        name="serviceType"
                        value={formData.serviceType}
                        onChange={handleChange}
                        style={{
                          width: '100%',
                          padding: '14px 16px',
                          background: 'rgba(255, 255, 255, 0.05)',
                          border: `1px solid ${formErrors.serviceType ? '#ff4444' : 'rgba(255, 255, 255, 0.2)'}`,
                          borderRadius: '8px',
                          color: formData.serviceType ? '#FFFFFF' : 'rgba(255, 255, 255, 0.5)',
                          fontSize: '16px',
                          outline: 'none',
                          transition: 'border-color 0.3s ease',
                          cursor: 'pointer'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#00A8CC'}
                        onBlur={(e) => e.target.style.borderColor = formErrors.serviceType ? '#ff4444' : 'rgba(255, 255, 255, 0.2)'}
                      >
                        <option value="">Bitte wählen...</option>
                        <option value="unfall">Unfallgutachten</option>
                        <option value="schaden">Schadengutachten</option>
                        <option value="bewertung">Fahrzeugbewertung</option>
                      </select>
                      {formErrors.serviceType && (
                        <span style={{ fontSize: '12px', color: '#ff4444', marginTop: '4px', display: 'block' }}>
                          {formErrors.serviceType}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Message */}
                  <div style={{ marginTop: '24px' }}>
                    <label style={{
                      display: 'block',
                      fontSize: '14px',
                      fontWeight: '600',
                      color: 'rgba(255, 255, 255, 0.9)',
                      marginBottom: '8px'
                    }}>
                      Nachricht (optional)
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={4}
                      style={{
                        width: '100%',
                        padding: '14px 16px',
                        background: 'rgba(255, 255, 255, 0.05)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: '8px',
                        color: '#FFFFFF',
                        fontSize: '16px',
                        outline: 'none',
                        transition: 'border-color 0.3s ease',
                        resize: 'vertical'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#00A8CC'}
                      onBlur={(e) => e.target.style.borderColor = 'rgba(255, 255, 255, 0.2)'}
                    />
                  </div>

                  {/* Privacy Checkbox */}
                  <div style={{ marginTop: '24px' }}>
                    <label style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '12px',
                      cursor: 'pointer'
                    }}>
                      <input
                        type="checkbox"
                        name="privacy"
                        checked={formData.privacy}
                        onChange={handleChange}
                        style={{
                          marginTop: '4px',
                          width: '18px',
                          height: '18px',
                          cursor: 'pointer'
                        }}
                      />
                      <span style={{
                        fontSize: '14px',
                        color: formErrors.privacy ? '#ff4444' : 'rgba(255, 255, 255, 0.7)',
                        lineHeight: '1.5'
                      }}>
                        Ich akzeptiere die Datenschutzerklärung und stimme der Verarbeitung meiner Daten zu. *
                      </span>
                    </label>
                    {formErrors.privacy && (
                      <span style={{ fontSize: '12px', color: '#ff4444', marginTop: '4px', display: 'block', marginLeft: '30px' }}>
                        {formErrors.privacy}
                      </span>
                    )}
                  </div>

                  {/* Submit Button */}
                  <div style={{ marginTop: '32px' }}>
                    <button
                      type="submit"
                      className="btn-primary"
                      disabled={formStatus === 'loading'}
                      style={{
                        width: '100%',
                        opacity: formStatus === 'loading' ? 0.7 : 1,
                        cursor: formStatus === 'loading' ? 'not-allowed' : 'pointer'
                      }}
                    >
                      {formStatus === 'loading' ? 'Wird gesendet...' : 'Schaden jetzt prüfen lassen'}
                    </button>
                  </div>

                  {/* Status Messages */}
                  {formStatus === 'success' && (
                    <div style={{
                      marginTop: '20px',
                      padding: '16px',
                      background: 'rgba(0, 168, 204, 0.1)',
                      border: '1px solid rgba(0, 168, 204, 0.3)',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      animation: 'fade-in-up 0.4s ease-out'
                    }}>
                      <CheckCircle size={24} style={{ color: '#00A8CC' }} />
                      <span style={{ color: '#00A8CC', fontSize: '16px', fontWeight: '500' }}>
                        Anfrage erfolgreich versendet! Wir melden uns in Kürze.
                      </span>
                    </div>
                  )}

                  {formStatus === 'error' && (
                    <div style={{
                      marginTop: '20px',
                      padding: '16px',
                      background: 'rgba(255, 68, 68, 0.1)',
                      border: '1px solid rgba(255, 68, 68, 0.3)',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      animation: 'fade-in-up 0.4s ease-out'
                    }}>
                      <AlertCircle size={24} style={{ color: '#ff4444' }} />
                      <span style={{ color: '#ff4444', fontSize: '16px', fontWeight: '500' }}>
                        Fehler beim Senden. Bitte versuchen Sie es erneut.
                      </span>
                    </div>
                  )}
                </form>
              </div>

              {/* WhatsApp Highlight */}
              <div className="glass-card" style={{
                marginTop: '24px',
                background: 'rgba(0, 168, 204, 0.05)',
                border: '1px solid rgba(0, 168, 204, 0.2)',
                textAlign: 'center',
                padding: '32px'
              }}>
                <h3 className="heading-2" style={{ marginBottom: '12px' }}>
                  Schnellster Weg: WhatsApp
                </h3>
                <p className="body-medium" style={{ 
                  color: 'rgba(255, 255, 255, 0.6)',
                  marginBottom: '20px'
                }}>
                  Direkt chatten und sofort Antwort erhalten
                </p>
                <button onClick={handleWhatsApp} className="btn-primary">
                  <MessageCircle size={20} />
                  WhatsApp öffnen
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
